import React, { useEffect, useState, useRef } from "react";
import { Loader2, Printer, X, Download, ShieldCheck, Building2, CheckCircle2 } from "lucide-react";
import * as api from "@/src/api/sdk.gen";
import type { InvoiceResponse } from "@/src/api/types.gen";

interface InvoiceModalProps {
  identifier: string; // transaction_id or checkout_id
  isOpen: boolean;
  onClose: () => void;
}

export default function InvoiceModal({ identifier, isOpen, onClose }: InvoiceModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<InvoiceResponse | null>(null);
  const invoiceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || !identifier) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const { data, error: apiErr } = await api.getPaymentInvoiceApiV1PaymentsInvoiceIdentifierGet({
          path: { identifier },
        });

        if (cancelled) return;

        if (apiErr || !data) {
          throw new Error("Could not load invoice data.");
        }

        setInvoice(data);
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || "Failed to load tax invoice.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, identifier]);

  if (!isOpen) return null;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto bg-slate-900/60 backdrop-blur-sm print:p-0 print:bg-white">
      <div className="relative w-full max-w-3xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden my-auto print:border-none print:shadow-none print:max-w-none print:my-0">
        {/* Modal Header Actions (hidden on print) */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/80 print:hidden">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-semibold text-slate-800">
              Tax Invoice & Payment Receipt
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
              ZATCA Compliant
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              disabled={loading || Boolean(error)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 transition shadow-sm disabled:opacity-50"
            >
              <Printer className="w-3.5 h-3.5" />
              Print / Save PDF
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
              aria-label="Close invoice"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 sm:p-8 max-h-[80vh] overflow-y-auto print:max-h-none print:overflow-visible print:p-8" ref={invoiceRef}>
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              <p className="text-sm text-slate-500 font-medium">Loading official tax invoice…</p>
            </div>
          )}

          {error && !loading && (
            <div className="text-center py-12">
              <p className="text-sm text-red-600 font-semibold">{error}</p>
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 text-xs font-semibold rounded-lg bg-slate-800 text-white"
              >
                Close
              </button>
            </div>
          )}

          {!loading && invoice && (
            <div className="space-y-6 text-slate-800">
              {/* Invoice Top Bar */}
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 pb-6 border-b border-slate-200">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold tracking-tight text-slate-900">
                      atomcamp / InaraX
                    </span>
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                      <CheckCircle2 className="w-3 h-3" /> PAID
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 max-w-sm">
                    {invoice.seller.legal_name}
                  </p>
                  <p className="text-xs text-slate-500">{invoice.seller.address}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    <span className="font-semibold text-slate-700">CR:</span> {invoice.seller.cr_number} |{" "}
                    <span className="font-semibold text-slate-700">VAT TRN:</span> {invoice.seller.vat_number}
                  </p>
                </div>

                <div className="text-left sm:text-right space-y-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Tax Invoice</p>
                  <p className="text-lg font-bold font-mono text-slate-900">{invoice.invoice_number}</p>
                  <p className="text-xs text-slate-600">
                    <span className="font-medium">Date: </span>
                    {new Date(invoice.issue_date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </p>
                  <p className="text-xs text-slate-600">
                    <span className="font-medium">Ref / Auth: </span>
                    <span className="font-mono">{invoice.checkout_id.slice(0, 18)}...</span>
                  </p>
                  {invoice.provider && (
                    <p className="text-xs text-slate-500 capitalize">
                      Settled via <span className="font-semibold">{invoice.provider}</span>
                      {invoice.payment_brand ? ` (${invoice.payment_brand})` : ""}
                    </p>
                  )}
                </div>
              </div>

              {/* Bill To & Corporate Information */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-slate-600" /> Bill To / Customer
                  </p>
                  <p className="text-sm font-bold text-slate-900">{invoice.buyer.company_name}</p>
                  {invoice.buyer.contact_email && (
                    <p className="text-xs text-slate-600">{invoice.buyer.contact_email}</p>
                  )}
                  {invoice.buyer.org_slug && (
                    <p className="text-xs text-slate-500 mt-1">
                      Organization Workspace: <span className="font-medium text-slate-700">{invoice.buyer.org_slug}</span>
                      {invoice.buyer.workspace_slug ? ` / ${invoice.buyer.workspace_slug}` : ""}
                    </p>
                  )}
                </div>

                <div className="space-y-1 text-xs">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                    Tax & Corporate Credentials
                  </p>
                  <div className="flex justify-between border-b border-slate-200/60 pb-1">
                    <span className="text-slate-500">VAT / Tax ID (TRN):</span>
                    <span className="font-mono font-semibold text-slate-800">{invoice.buyer.company_vat_number}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200/60 pb-1">
                    <span className="text-slate-500">Commercial Reg. (CR):</span>
                    <span className="font-mono font-semibold text-slate-800">{invoice.buyer.company_cr_number}</span>
                  </div>
                  <div className="flex justify-between pt-0.5">
                    <span className="text-slate-500">Purchase Order (PO #):</span>
                    <span className="font-mono font-semibold text-indigo-700">{invoice.buyer.purchase_order_number}</span>
                  </div>
                </div>
              </div>

              {/* Line Items Table */}
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-100/80 text-slate-700 font-semibold border-b border-slate-200 uppercase tracking-wider">
                    <tr>
                      <th className="py-2.5 px-4">Item Description</th>
                      <th className="py-2.5 px-4 text-center">Type / Scope</th>
                      <th className="py-2.5 px-4 text-center">Qty</th>
                      <th className="py-2.5 px-4 text-right">Unit Price ({invoice.currency})</th>
                      <th className="py-2.5 px-4 text-right">Amount ({invoice.currency})</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-800">
                    {(invoice.line_items && invoice.line_items.length > 0 ? invoice.line_items : [
                      {
                        description: invoice.item_title,
                        quantity: 1,
                        unit_price: invoice.base_amount,
                        total_price: invoice.base_amount,
                      }
                    ]).map((item, idx) => (
                      <tr key={idx} className="hover:bg-slate-50/50">
                        <td className="py-3 px-4 font-medium text-slate-900">
                          {item.description}
                          {invoice.seat_limit && idx === 0 && (
                            <span className="block text-[11px] text-slate-500">
                              Access for up to {invoice.seat_limit} workspace seats
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center text-slate-500">
                          {idx === 0 ? invoice.item_type : "Add-on Bundle"}
                        </td>
                        <td className="py-3 px-4 text-center text-slate-700 font-mono">
                          {item.quantity}
                        </td>
                        <td className="py-3 px-4 text-right text-slate-700 font-mono">
                          {item.unit_price.toFixed(2)}
                        </td>
                        <td className="py-3 px-4 text-right font-semibold text-slate-900 font-mono">
                          {item.total_price.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Breakdown & Totals */}
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4 pt-2">
                <div className="max-w-xs text-[11px] text-slate-500 space-y-1">
                  <p className="font-semibold text-slate-700">Notice:</p>
                  <p>
                    This is an electronically generated and validated corporate tax invoice. It serves as an official receipt under applicable electronic invoicing regulations.
                  </p>
                </div>

                <div className="w-full sm:w-64 space-y-2 text-xs">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal (excl. Tax):</span>
                    <span className="font-mono font-medium">{invoice.currency} {invoice.subtotal_ex_vat.toFixed(2)}</span>
                  </div>
                  {invoice.discount_amount > 0 && (
                    <div className="flex justify-between text-emerald-600 font-medium">
                      <span>Discount:</span>
                      <span className="font-mono">-{invoice.currency} {invoice.discount_amount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-slate-600">
                    <span>VAT ({invoice.vat_rate}%):</span>
                    <span className="font-mono font-medium">{invoice.currency} {invoice.vat_amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-300 pt-2 text-sm font-bold text-slate-900">
                    <span>Total Paid:</span>
                    <span className="font-mono text-base text-indigo-700">
                      {invoice.currency} {invoice.total_amount.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between text-[11px] text-slate-400 gap-2">
                <p>Thank you for partnering with atomcamp & InaraX.</p>
                <p>Inquiries: finance@inarax.ai</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
